import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import ReactHtmlParser from 'react-html-parser';
import useResponseUser from '../../hooks/useResponseUser';
import * as aiService from '../../services/ai.service';
import { getBeneficiaryById } from '../../services/user.service';
import { getFromLS } from '../../utils/utils';
import { useStyles } from './style';

interface IASynthesisBlockProps {
  userId: string;
  responseTestId: string;
  testName: string;
  onGenerate?: () => Promise<string>;
}

const IASynthesisBlock: FC<IASynthesisBlockProps> = ({
  userId,
  responseTestId,
  testName,
  onGenerate,
}) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [synthesisText, setSynthesisText] = useState<string | null>(null);
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [isTextOverflowing, setIsTextOverflowing] = useState(true);
  const [isCheckingExistence, setIsCheckingExistence] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const textContentRef = useRef<HTMLDivElement>(null);
  const { getResponseTestUser } = useResponseUser();

  const checkTextHeight = useCallback(() => {
    if (!textContentRef.current || !synthesisText || isTextExpanded) return;

    const element = textContentRef.current;
    const rect = element.getBoundingClientRect();

    if (rect.height === 0) {
      setTimeout(checkTextHeight, 50);
      return;
    }

    const originalMaxHeight = element.style.maxHeight;
    const originalOverflow = element.style.overflow;

    element.style.maxHeight = 'none';
    element.style.overflow = 'visible';

    const naturalHeight = element.scrollHeight;

    element.style.maxHeight = originalMaxHeight;
    element.style.overflow = originalOverflow;

    setIsTextOverflowing(naturalHeight > 509);
  }, [synthesisText, isTextExpanded]);

  useEffect(() => {
    const checkExistingSynthesis = async () => {
      try {
        const result = await aiService.checkTestSynthesis(responseTestId);
        if (result.exists && result.synthesis) {
          setSynthesisText(result.synthesis);
          setTimeout(() => {
            checkTextHeight();
          }, 100);
        }
      } finally {
        setIsCheckingExistence(false);
      }
    };

    checkExistingSynthesis();
  }, [responseTestId, checkTextHeight]);

  const handleGenerateSynthesis = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const token = getFromLS('userToken') || '';

      const [identityResponse, responseTest] = await Promise.all([
        getBeneficiaryById({ userId, token, needTestDone: false }),
        getResponseTestUser(responseTestId),
      ]);

      const synthesis = await aiService.generateTestSynthesis(
        userId,
        responseTestId,
        testName,
        identityResponse.data,
        responseTest
      );

      setSynthesisText(synthesis);
      setTimeout(() => {
        checkTextHeight();
      }, 100);
    } catch (error: any) {
      // Check if error is synthesis refusal from content moderation
      if (error?.message?.includes('SYNTHESIS_REFUSED') || error?.response?.data?.message?.includes('SYNTHESIS_REFUSED')) {
        setErrorMessage(
          'La génération de la synthèse a été refusée par notre système de sécurité. Certaines données de votre test ont déclenché nos filtres de modération. Veuillez réessayer ultérieurement ou contacter notre support si le problème persiste.'
        );
      } else {
        setErrorMessage(
          'Une erreur est survenue lors de la génération de votre synthèse. Veuillez réessayer ultérieurement.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (synthesisText) {
    const shouldShowButton = isTextOverflowing && !isTextExpanded;

    return (
      <div className={classes.container}>
        <div className={classes.contentBlock}>
          <div className={classes.header}>
            <Typography className={classes.headerText}>
              Vos résultats du test approfondi avec l&apos;IA
            </Typography>
          </div>

          <div style={{ position: 'relative' }}>
            <div
              ref={textContentRef}
              style={{
                maxHeight: isTextOverflowing && !isTextExpanded ? 509 : 'none',
                overflow: isTextOverflowing && !isTextExpanded ? 'hidden' : 'visible',
              }}>
              <Typography
                className={classes.contentText}
                style={{
                  marginBottom: shouldShowButton ? 0 : 16,
                }}>
                {ReactHtmlParser(synthesisText)}
              </Typography>

              {isTextOverflowing && isTextExpanded && (
                <div style={{ textAlign: 'center', margin: '16px 20px 0 20px' }}>
                  <Button
                    variant="text"
                    onClick={() => setIsTextExpanded(false)}
                    className={classes.seeLessButton}>
                    Voir moins
                  </Button>
                </div>
              )}

              {isTextOverflowing && !isTextExpanded && <div className={classes.fadeOverlay} />}
            </div>

            {shouldShowButton && (
              <Button
                className={classes.showMoreButton}
                variant="outlined"
                onClick={() => setIsTextExpanded(true)}>
                Afficher le reste de mes résultats
                <img
                  src="/icons/icon-text-plus.svg"
                  alt=""
                  style={{
                    width: 16,
                    height: 16,
                    marginLeft: 8,
                    marginBottom: '0.2rem',
                  }}
                />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isCheckingExistence || isLoading) {
    return (
      <div className={classes.container}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={24} style={{ color: '#FAFAFA', marginRight: 12 }} />
          <Typography className={classes.loadingText}>
            {isCheckingExistence ? 'Vérification...' : 'Analyse en cours'}
          </Typography>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className={classes.container}>
        <div className={classes.contentBlock}>
          <div className={classes.header}>
            <Typography className={classes.headerText}>
              Impossible de générer la synthèse
            </Typography>
          </div>
          <div style={{ padding: '20px' }}>
            <Typography
              style={{
                color: '#FAFAFA',
                fontSize: '14px',
                lineHeight: '1.6',
                marginBottom: '20px',
              }}>
              {errorMessage}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setErrorMessage(null);
                handleGenerateSynthesis();
              }}
              style={{
                color: '#FAFAFA',
                borderColor: '#FAFAFA',
                textTransform: 'none',
              }}>
              Réessayer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.initialContainer}>
        <Typography className={classes.initialText}>
          Explorez de nouvelles frontières avec la force de l&apos;IA
        </Typography>
        <Button
          variant="contained"
          className={classes.generateButton}
          onClick={handleGenerateSynthesis}
          endIcon={
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginLeft: 8 }}>
              <path
                d="M12 8L8 4M12 8L8 12M12 8H4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }>
          Je veux approfondir mes résultats
        </Button>
      </div>
    </div>
  );
};

export default IASynthesisBlock;
